import { friendlyErrorMessage } from '../../shared/friendlyError';
import { PayrollHistoryApiError, type BatchStatus } from './types';

export const COMMIT_FAILED_MESSAGE =
  'The import could not be completed and was rolled back. Nothing was imported.';

const TRUSTED_STATUSES = [400, 404, 409, 413, 415];

/**
 * User-facing message for a failed call.
 *  - COMMIT_FAILED (500) always means "rolled back, nothing imported", so it gets its own safe wording.
 *  - The Payroll History API's 4xx problem `detail`s are authored for users, so they are shown as-is, but ONLY
 *    when the server actually supplied one (`serverMessage`). A proxy's bare 413 or a framework 404/400 has no
 *    such detail and gets neutral wording instead of the client's fabricated "Request to ... failed" text.
 *  - Everything else (401, 403, 5xx, network) goes through the shared friendlyErrorMessage.
 */
export function describeError(err: unknown, fallback: string): string {
  if (err instanceof PayrollHistoryApiError) {
    if (err.code === 'COMMIT_FAILED') return COMMIT_FAILED_MESSAGE;
    if (TRUSTED_STATUSES.includes(err.status)) {
      if (err.serverMessage && err.message) return err.message;
      if (err.status === 413) return 'The file is larger than the maximum allowed size.';
      if (err.status === 415) return 'Only a .zip package can be uploaded.';
      // 400/404/409 with no server detail: log through friendlyErrorMessage (as a non-API error) but never show
      // the client-fabricated text.
      return friendlyErrorMessage(new Error(`HTTP ${err.status} without a problem detail`), fallback);
    }
  }
  return friendlyErrorMessage(err, fallback);
}

export function isNotFound(err: unknown): boolean {
  return err instanceof PayrollHistoryApiError && err.status === 404;
}

export function isConflict(err: unknown): boolean {
  return err instanceof PayrollHistoryApiError && err.status === 409;
}

const TERMINAL: BatchStatus[] = ['Completed', 'CompletedWithWarnings', 'Failed', 'Canceled'];
export const isTerminalStatus = (status: BatchStatus): boolean => TERMINAL.includes(status);

export const NOTHING_TO_EXPORT_MESSAGE = 'There is no imported payroll history to export yet.';
export const EXPORT_NO_ACCESS_MESSAGE = "You don't have access to export payroll history. Ask a company administrator if you need it.";

/** The wording for a failed export: an empty history and a missing permission are explained, not shown as faults. */
export function describeExportError(err: unknown): string {
  if (err instanceof PayrollHistoryApiError) {
    if (err.status === 404 && err.code === 'NOTHING_TO_EXPORT') return NOTHING_TO_EXPORT_MESSAGE;
    if (err.status === 403) return EXPORT_NO_ACCESS_MESSAGE;
  }
  return describeError(err, 'Could not export payroll history.');
}
